import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { logError } from "@src/appV2/lib/analytics/log";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import pluralize from "pluralize";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { useDefinedWorker } from "../../../../Worker/useDefinedWorker";
import { DialogFooter } from "../../../components/DialogFooter";
import { LoadingButton } from "../../../components/LoadingButton";
import { SHIFT_DISCOVERY_SHIFT_BUNDLE_BOOKED_MODAL_PATH } from "../../../ShiftDiscovery/paths";
import { useShiftModalsDataContext } from "../../../ShiftDiscovery/useShiftModalsDataContext";
import { performChecksBeforeBooking, PreBookingCheck } from "../../Booking/useBookShift";
import { useCheckPreBookingState } from "../../Booking/useCheckPreBookingState";
import { getBackgroundCheckContext } from "../../getBackgroundCheckContext";
import { type ModalShiftData } from "../../types";
import { useBookBundleShifts } from "../hooks/useBookBundleShifts";
import { type ShiftBundle } from "../types";

interface SelectionFooterProps {
  shift: ModalShiftData;
  bundle: ShiftBundle;
  selectedShiftIds: Set<string>;
  selectedCount: number;
  totalSelectedPayInMinorUnits: number | undefined;
  isBookingAllowed: boolean;
}

export function SelectionFooter(props: SelectionFooterProps) {
  const {
    shift,
    bundle,
    selectedShiftIds,
    selectedCount,
    totalSelectedPayInMinorUnits,
    isBookingAllowed,
  } = props;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const { navigateToModal, setInitialShiftModalsData, bookabilityStatus } =
    useShiftModalsDataContext();

  const { missingDataTypes: missingBackgroundCheckDataTypes } =
    getBackgroundCheckContext(bookabilityStatus);

  const { geolocationIsUnavailable, deviceGeolocation, isPreBookingChecksLoading } =
    useCheckPreBookingState({
      workplaceId: shift.workplace.id,
      shiftId: shift.id,
    });

  const worker = useDefinedWorker();

  const { bookBundleShifts } = useBookBundleShifts(bundle.shifts);

  const handleBook = async () => {
    setIsProcessing(true);

    try {
      // Run pre-booking checks that don't auto-book on resolution.
      // We skip BOOKING_CONFIRMATION because the selection screen is the confirmation.
      // We skip BREAK_POLICY_ACKNOWLEDGEMENT and BASKET_SIZE_WILL_REACH_WARNING, and
      // set shouldEducateNewWorker, shouldShowClipboardScoreEducation, and
      // isBasketSizeWarningAcknowledgmentNeeded to false because bundles are excluded
      // for workers who would trigger those modals (see useShiftBundles).
      const shouldProceed = performChecksBeforeBooking({
        missingBackgroundCheckDataTypes,
        isBasketSizeWarningAcknowledgmentNeeded: false,
        geolocationIsUnavailable,
        requiresBreakPolicyAcknowledgement: false,
        navigateToModal,
        shiftId: shift.id,
        skipChecks: [
          PreBookingCheck.BOOKING_CONFIRMATION,
          PreBookingCheck.BASKET_SIZE_WILL_REACH_WARNING,
          PreBookingCheck.BREAK_POLICY_ACKNOWLEDGEMENT,
        ],
        shouldEducateNewWorker: false,
        shouldShowArrivalTimeModal: false,
        shouldShowClipboardScoreEducation: false,
        history,
      });

      if (!shouldProceed) {
        return;
      }

      const results = await bookBundleShifts(selectedShiftIds, deviceGeolocation?.geoLocation);

      const failedShiftIds = results.failedShifts.map((f) => f.shiftId);

      for (const failedShift of results.failedShifts) {
        logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_BOOKING_FAILURE, {
          error: failedShift.error,
          metadata: {
            shiftId: failedShift.shiftId,
            workplaceId: bundle.workplaceId,
            workerId: worker.userId,
            bundleShiftCount: bundle.totalShiftCount,
          },
        });
      }

      if (results.bookedShiftIds.length === 0) {
        showErrorToast("Something went wrong while booking these shifts. Please try again.");
        return;
      }

      logEvent(APP_V2_USER_EVENTS.SHIFT_BUNDLE_BOOKED, {
        workplaceId: bundle.workplaceId,
        workerId: worker.userId,
        anchorShiftId: shift.id,
        qualification: bundle.qualification,
        totalShiftCount: bundle.totalShiftCount,
        requiredShiftCount: bundle.requiredShiftCount,
        bookedShiftIds: results.bookedShiftIds,
      });

      setInitialShiftModalsData((previous) => ({
        ...previous,
        bundle,
        bundleBookingResults: {
          bookedShiftIds: results.bookedShiftIds,
          failedShiftIds,
        },
      }));

      navigateToModal(SHIFT_DISCOVERY_SHIFT_BUNDLE_BOOKED_MODAL_PATH, {
        pathParams: { shiftId: shift.id },
      });
    } catch (error) {
      logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_BOOKING_FAILURE, {
        error,
        metadata: {
          workplaceId: bundle.workplaceId,
          bundleShiftCount: bundle.totalShiftCount,
          selectedShiftIds: [...selectedShiftIds],
        },
      });
      showErrorToast("Something went wrong while booking these shifts. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <DialogFooter withShadow orientation="vertical">
      <Stack sx={{ width: "100%", paddingX: 1, gap: 4 }}>
        <Text semibold variant="body1">
          {selectedCount === 0
            ? "No shifts selected yet"
            : `${selectedCount} ${pluralize("shift", selectedCount)}${
                isDefined(totalSelectedPayInMinorUnits)
                  ? `, totaling ${formatCentsAsUsd(totalSelectedPayInMinorUnits)}`
                  : ""
              }`}
        </Text>

        <LoadingButton
          fullWidth
          disabled={!isBookingAllowed || isProcessing || isPreBookingChecksLoading}
          isLoading={isProcessing}
          variant="contained"
          size="large"
          onClick={handleBook}
        >
          {isBookingAllowed || selectedCount >= bundle.requiredShiftCount
            ? `Book ${selectedCount} ${pluralize("shift", selectedCount)}`
            : `Select ${bundle.requiredShiftCount - selectedCount} more ${pluralize("shift", bundle.requiredShiftCount - selectedCount)}`}
        </LoadingButton>
      </Stack>
    </DialogFooter>
  );
}
